import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../store/slices/authSlice';
import { showToast } from '../utils/Toast';
import NewLead from '../leads/NewLead';
import { deleteLead } from '../../store/slices/leadsSlice';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import Spinner from '../common/Spinner';
import LeadDetailsDialog from '../leads/LeadDetailsDialog';
import EditProfileDialog from '../profile/EditProfileDialog';
import { getInitials } from '../../utils/stringUtils';
import { useThemeColors } from '../../hooks/useThemeColors';

const DashboardHeader = ({ 
  hideCreateLead = false, 
  onCustomizeFields, 
  activeTab, 
  onTabChange, 
  selectedItems = [],
  selectedLeads = [],
  onSearch,
  searchTerm = '',
  hideActions = false,
  hideTabBar = false,
  hideSearch = false,
  items = [],
  leads = [],
  onDelete,
  tabs = [],
  createButtonText = 'Create New Lead',
  onNewClick,
  visibleColumns = [],
  filters = null,
  onPriorityChange,
  onClearAllFilters
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { buttonColors } = useThemeColors();
  
  // Add state for dropdown
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  // Add new state for profile dropdown
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  // Add state for new lead dialog
  const [showNewLead, setShowNewLead] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  // Add new state for edit dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showEditProfile, setShowEditProfile] = useState(false);
  // Mobile menu collapse removed - always show menu on mobile/tablet
  // Add state for mobile tabs collapse
  const [mobileTabsCollapsed, setMobileTabsCollapsed] = useState(true);
  // Add state for mobile search visibility
  const [mobileSearchVisible, setMobileSearchVisible] = useState(false);
  
  // New states for enhanced search functionality
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [showSearchColumns, setShowSearchColumns] = useState(false);
  const [selectedSearchColumn, setSelectedSearchColumn] = useState('all');
  const [searchInputValue, setSearchInputValue] = useState(searchTerm || '');
  
  // Filters popover state
  const [showFiltersPopover, setShowFiltersPopover] = useState(false);
  const filtersPopoverRef = useRef(null);

  // References for handling clicks outside dropdowns
  const searchColumnRef = useRef(null);
  const searchBarRef = useRef(null);
  const mobileTabsRef = useRef(null);
  const mobileSearchInputRef = useRef(null);

  // Add useEffect to handle clicks outside dropdowns and dialog backdrops
  useEffect(() => {
    function handleClickOutside(event) {
      // Desktop dropdown handling
      if (searchColumnRef.current && !searchColumnRef.current.contains(event.target)) {
        setShowSearchColumns(false);
      }
      if (mobileTabsRef.current && !mobileTabsRef.current.contains(event.target)) {
        setMobileTabsCollapsed(true);
      }
      if (filtersPopoverRef.current && !filtersPopoverRef.current.contains(event.target)) {
        setShowFiltersPopover(false);
      }
    }
    
    function handleEscapeKey(event) {
      if (event.key === 'Escape') {
        setShowProfileDropdown(false);
        setShowSearchColumns(false);
        setShowActionDropdown(false);
        setMobileTabsCollapsed(true);
        setShowFiltersPopover(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  // Auto-focus mobile search input when it becomes visible
  useEffect(() => {
    console.log('Mobile search visibility changed:', mobileSearchVisible);
    if (mobileSearchVisible && mobileSearchInputRef.current) {
      setTimeout(() => {
        mobileSearchInputRef.current?.focus();
      }, 200); // Wait for animation to complete
    }
  }, [mobileSearchVisible]);

  // Determine available search columns based on context
  const getSearchColumns = () => {
    // Default column - always include "All Columns"
    let searchColumns = [
      { id: 'all', label: 'All Columns' }
    ];

    // If visibleColumns are provided, use those first
    if (Array.isArray(visibleColumns) && visibleColumns.length > 0) {
      visibleColumns.forEach(column => {
        if (column.key || column.id) {
          const columnId = column.key || column.id;
          let columnLabel = column.label || column.name;
          
          // If we have a nested path like 'client.name', format it nicely
          if (columnId.includes('.')) {
            const parts = columnId.split('.');
            columnLabel = parts
              .map(part => 
                part.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
              )
              .join(' › ');
          }
          
          searchColumns.push({ 
            id: columnId, 
            label: columnLabel 
          });
        }
      });
    } 
    // If no visibleColumns but we have items or leads, extract columns from them
    else if (Array.isArray(items) && items.length > 0) {
      // We're in a page with items (clients, projects)
      // Get all possible keys from the items
      const keys = new Set();
      
      items.forEach(item => {
        Object.keys(item).forEach(key => {
          if (key !== '_id' && key !== '__v' && !key.startsWith('_')) {
            // For nested objects, add both the parent key and the nested keys
            if (typeof item[key] === 'object' && item[key] !== null) {
              keys.add(key);
              Object.keys(item[key] || {}).forEach(nestedKey => {
                keys.add(`${key}.${nestedKey}`);
              });
            } else {
              keys.add(key);
            }
          }
        });
      });
      
      // Add all keys as search options
      keys.forEach(key => {
        // Convert camelCase to Title Case for the label
        let label = key
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase());
        
        // Handle nested properties
        if (key.includes('.')) {
          const parts = key.split('.');
          label = parts
            .map(part => 
              part.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
            )
            .join(' › ');
        }
        
        searchColumns.push({ id: key, label });
      });
    } else if (Array.isArray(leads) && leads.length > 0) {
      // We're in the leads page
      // Get all possible keys from the leads
      const keys = new Set();
      
      leads.forEach(lead => {
        Object.keys(lead).forEach(key => {
          if (key !== '_id' && key !== '__v' && !key.startsWith('_')) {
            // For nested objects, add both the parent key and the nested keys
            if (typeof lead[key] === 'object' && lead[key] !== null) {
              keys.add(key);
              Object.keys(lead[key] || {}).forEach(nestedKey => {
                keys.add(`${key}.${nestedKey}`);
              });
            } else {
              keys.add(key);
            }
          }
        });
      });
      
      // Add all keys as search options
      keys.forEach(key => {
        // Convert camelCase to Title Case for the label
        let label = key
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase());
        
        // Handle nested properties
        if (key.includes('.')) {
          const parts = key.split('.');
          label = parts
            .map(part => 
              part.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
            )
            .join(' › ');
        }
        
        searchColumns.push({ id: key, label });
      });
    } else {
      // Fallback to default columns based on URL path
      if (!hideCreateLead) {
        // For Leads context
        searchColumns = [
          { id: 'all', label: 'All Columns' },
          { id: 'jobNumber', label: 'Job Number' },
          { id: 'leadContact.name', label: 'Lead Contact' },
          { id: 'leadContact.email', label: 'Lead Email' },
          { id: 'address.street', label: 'Street Address' },
          { id: 'address.city', label: 'City' },
          { id: 'client.name', label: 'Client Name' },
          { id: 'leadStatus', label: 'Lead Status' },
                      { id: 'priority', label: 'Priority' }
        ];
      } else if (window.location.pathname.includes('/clients')) {
        // For Clients context
        searchColumns = [
          { id: 'all', label: 'All Columns' },
          { id: 'name', label: 'Name' },
          { id: 'email', label: 'Email' },
          { id: 'cellPhone', label: 'Phone' },
          { id: 'address.city', label: 'City' },
          { id: 'address.state', label: 'State' }
        ];
      } else if (window.location.pathname.includes('/projects')) {
        // For Projects context
        searchColumns = [
          { id: 'all', label: 'All Columns' },
          { id: 'jobNumber', label: 'Job Number' },
          { id: 'leadContact.name', label: 'Lead Contact' },
          { id: 'projectManagers', label: 'Assigned To' },
          { id: 'client.name', label: 'Client' },
          { id: 'address.city', label: 'Location' },
          { id: 'projectStatus', label: 'Status' },
          { id: 'priority', label: 'Priority' }
        ];
      }
    }

    // Sort columns alphabetically by label, keeping "All Columns" at the top
    return [
      searchColumns[0], // Keep "All Columns" at the top
      ...searchColumns.slice(1).sort((a, b) => a.label.localeCompare(b.label))
    ];
  };

  // Add debounced search handler
  const debouncedSearch = React.useCallback(
    debounce((value, column) => {
      if (onSearch) {
        console.log('Searching with value:', value, 'column:', column);
        if (column === 'all') {
          onSearch(value);
        } else {
          onSearch(value, column);
        }
      }
    }, 300),
    [onSearch]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    debouncedSearch(value, selectedSearchColumn);
  };

  const handleSearchColumnSelect = (columnId) => {
    setSelectedSearchColumn(columnId);
    setShowSearchColumns(false);
    // Re-run search with new column
    debouncedSearch(searchInputValue, columnId);
  };

  const handleSearchFocus = () => {
    setIsSearchExpanded(true);
  };

  const handleSearchBlur = (e) => {
    // Only collapse if clicking outside of search area
    if (!searchBarRef.current?.contains(e.relatedTarget)) {
      // Don't collapse if there's text
      if (!searchInputValue) {
        setIsSearchExpanded(false);
      }
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    showToast('Logged out successfully', 'success');
    navigate('/login');
  };

  const handleDeleteClick = () => {
    const itemsToCheck = selectedItems.length > 0 ? selectedItems : selectedLeads;
    
    if (itemsToCheck.length === 0) {
      showToast('Please select an item to delete', 'warning');
      return;
    }

    if (onDelete) {
      onDelete();
    } else {
      setShowDeleteDialog(true);
    }
    setShowActionDropdown(false);
  };

  const handleDeleteConfirm = async () => {
    try {
      const itemsToDelete = selectedItems.length > 0 ? selectedItems : selectedLeads;
      
      for (const id of itemsToDelete) {
        await dispatch(deleteLead(id)).unwrap();
      }
      setShowDeleteDialog(false);
      showToast('Lead(s) deleted successfully', 'success');
    } catch (error) {
      console.error('Failed to delete:', error);
      showToast('Failed to delete item(s)', 'error');
    }
  };

  const handleEditClick = () => {
    const itemsToCheck = selectedItems.length > 0 ? selectedItems : selectedLeads;
    const itemsList = items.length > 0 ? items : leads;

    if (itemsToCheck.length !== 1) {
      showToast('Please select one item to edit', 'warning');
      return;
    }

    const fullItem = itemsList.find(item => item._id === itemsToCheck[0]);
    if (fullItem) {
      if (!hideCreateLead) {
        const transformedLead = {
          _id: fullItem._id,
          opportunityType: fullItem.opportunityType,
          streetAddress: fullItem.address?.street || '',
          city: fullItem.address?.city || '',
          state: fullItem.address?.state || '',
          zipCode: fullItem.address?.zipCode || '',
          mapLink: fullItem.mapLink || '',
          projectManagers: fullItem.projectManagers || [],
          
          // Client Information
          client: fullItem.client,
          clientName: fullItem.client?.name || '',
          clientAddress: fullItem.client?.address?.street || '',
          clientCity: fullItem.client?.address?.city || '',
          clientState: fullItem.client?.address?.state || '',
          clientZipCode: fullItem.client?.address?.zipCode || '',
          clientCellPhone: fullItem.client?.phone || '',
          clientAltPhone: fullItem.client?.altPhone || '',
          clientEmail: fullItem.client?.email || '',
          
          // Site Contact Information
          siteContact: fullItem.siteContact,
          siteContactName: fullItem.siteContact?.name || '',
          siteContactAddress: fullItem.siteContact?.address?.street || '',
          siteContactCity: fullItem.siteContact?.address?.city || '',
          siteContactState: fullItem.siteContact?.address?.state || '',
          siteContactZipCode: fullItem.siteContact?.address?.zipCode || '',
          siteContactCellPhone: fullItem.siteContact?.cellPhone || '',
          siteContactAltPhone: fullItem.siteContact?.altPhone || '',
          siteContactEmail: fullItem.siteContact?.email || '',
          
          // Notes and Attachments
          notes: fullItem.notes || [],
          attachments: fullItem.attachments || [],
          
          // Additional fields
          jobNumber: fullItem.jobNumber,
          status: fullItem.status,
          priority: fullItem.priority,
          leadStatus: fullItem.leadStatus,
        };
        setSelectedLead(transformedLead);
        setShowEditDialog(true);
      }
      setShowActionDropdown(false);
    }
  };

  // Use the tabs from props if provided, otherwise use default tabs
  const displayTabs = tabs.length > 0 ? tabs : (activeTab ? (
    hideCreateLead ? [
      // Project tabs
      { id: 'active', label: 'Active Projects' },
      { id: 'priority', label: 'Priority' },
      { id: 'archived', label: 'Archives' }
    ] : [
      // Lead tabs
      { id: 'open', label: 'Open Leads' },
      { id: 'recent', label: 'Recently Sent' },
      { id: 'priority', label: 'Priority' },
      { id: 'archived', label: 'Archives' }
    ]
  ) : []);

  // Add search loading state from Redux
  const { loading: searchLoading } = useSelector(state => state.search);

  // Get the appropriate search columns
  const searchColumns = getSearchColumns();

  // Get current column label
  const currentColumnLabel = searchColumns.find(col => col.id === selectedSearchColumn)?.label || 'All Columns';

  return (
    <>
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        @keyframes slide-down {
          from {
            transform: translateY(-10px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-down {
          animation: slide-down 0.2s ease-out;
        }
      `}</style>
      <header className="bg-white border-b">
        {/* Top Section */}
        <div className="p-6">
          <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
            <div className="flex justify-between items-center w-full xl:w-auto">
              <div>
                <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
                  Welcome Back, <span className="text-blue-600">{user?.profile?.firstName || 'User'}</span>
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                </h1>
                <p className="text-gray-500 text-sm mt-1 flex items-center gap-2">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {new Date().toLocaleDateString('en-US', { 
                    weekday: 'short', 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
              
              {/* Mobile icons: Search and Profile - right-aligned */}
              <div className="xl:hidden flex items-center gap-3">
                {/* Mobile Search Icon - only show if hideSearch is false */}
                {!hideSearch && (
                  <button
                    className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
                    onClick={() => {
                      console.log('Mobile search toggle clicked, current state:', mobileSearchVisible);
                      setMobileSearchVisible(!mobileSearchVisible);
                    }}
                    aria-label="Toggle search"
                  >
                    {mobileSearchVisible ? (
                      <svg className="w-5 h-5 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                      </svg>
                    )}
                  </button>
                )}
                
                <div className="relative">
                  <button 
                    className="relative w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors overflow-hidden"
                    onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  >
                    {user?.profile?.profilePicture ? (
                      <img 
                        src={user.profile.profilePicture}
                        alt={`${user.profile.firstName} ${user.profile.lastName}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                        {getInitials(user?.profile?.firstName, user?.profile?.lastName)}
                      </div>
                    )}
                  </button>
                </div>
              </div>
            </div>
            
                        {/* Desktop layout: Create button, Search, Profile in one row */}
            <div className="hidden xl:flex items-center gap-4 w-auto">
              {/* Desktop Create New Lead Button */}
              {!hideCreateLead && (
                <button 
                  className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors"
                  style={{
                    backgroundColor: buttonColors.primary,
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = buttonColors.primaryHover}
                  onMouseLeave={(e) => e.target.style.backgroundColor = buttonColors.primary}
                  onClick={onNewClick || (() => setShowNewLead(true))}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                  </svg>
                  {createButtonText}
                </button>
              )}

              {/* Desktop Search bar */}
              {!hideSearch && (
                <div 
                  ref={searchBarRef}
                  className="relative search-bar transition-all duration-200 ease-in-out flex-1 max-w-2xl"
                >
                  <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200 focus-within:border-blue-300 focus-within:ring-1 focus-within:ring-blue-200 transition-all">
                    <div className="relative">
                      <button
                        className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-gray-800 border-r border-gray-200"
                        onClick={() => setShowSearchColumns(!showSearchColumns)}
                      >
                        <span className="text-sm whitespace-nowrap max-w-[100px] truncate">
                          {currentColumnLabel}
                        </span>
                        <svg 
                          className={`w-4 h-4 transition-transform ${showSearchColumns ? 'rotate-180' : ''}`} 
                          viewBox="0 0 24 24" 
                          fill="none"
                        >
                          <path d="M19 9l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      
                      {/* Column selection dropdown - Desktop only */}
                      {showSearchColumns && (
                        <div 
                          ref={searchColumnRef}
                          className="absolute left-0 top-full mt-1 w-48 bg-white rounded-lg border border-gray-200 shadow-lg z-50 max-h-64 overflow-y-auto"
                        >
                          {searchColumns.map(column => (
                            <button
                              key={column.id}
                              className={`w-full px-4 py-2 text-left hover:bg-gray-50 ${
                                selectedSearchColumn === column.id 
                                  ? 'bg-blue-50 text-blue-600' 
                                  : 'text-gray-700'
                              }`}
                              onClick={() => handleSearchColumnSelect(column.id)}
                            >
                              {column.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <input 
                      type="text" 
                      placeholder={`Search ${currentColumnLabel.toLowerCase()}`}
                      className="w-full py-2 pl-3 pr-10 bg-transparent rounded-lg text-gray-700 placeholder-gray-400 border-none focus:outline-none focus:ring-0"
                      onChange={handleSearchChange}
                      onFocus={handleSearchFocus}
                      onBlur={handleSearchBlur}
                      value={searchInputValue}
                    />
                    
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {searchLoading ? (
                        <Spinner size="sm" color="blue" />
                      ) : (
                        <svg 
                          className="w-4 h-4 text-gray-400" 
                          viewBox="0 0 24 24" 
                          fill="none"
                        >
                          <path 
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Desktop Profile */}
              <div className="flex items-center gap-3">
                <div className="relative">
                  <button 
                    className="relative w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors overflow-hidden"
                    onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  >
                    {user?.profile?.profilePicture ? (
                      <img 
                        src={user.profile.profilePicture}
                        alt={`${user.profile.firstName} ${user.profile.lastName}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                        {getInitials(user?.profile?.firstName, user?.profile?.lastName)}
                      </div>
                    )}
                  </button>

                  {/* Profile Dropdown Menu - Desktop only */}
                  {showProfileDropdown && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg border border-gray-200 shadow-lg z-20">
                      <div className="p-4 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900">
                          {`${user?.profile?.firstName} ${user?.profile?.lastName}`}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">{user?.email}</p>
                      </div>
                      
                      <div className="py-2">
                        <button 
                          className="w-full px-4 py-2.5 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                          onClick={() => {
                            setShowProfileDropdown(false);
                            setShowEditProfile(true);
                          }}
                        >
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                          </svg>
                          Edit Profile
                        </button>
                        
                        <button 
                          className="w-full px-4 py-2.5 text-left text-red-600 hover:bg-red-50 flex items-center gap-3"
                          onClick={handleLogout}
                        >
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
                          </svg>
                          Logout
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>


          </div>

          {/* Mobile Search Bar */}
          {!hideSearch && mobileSearchVisible && (
            <div className="xl:hidden mt-4 bg-white p-3 rounded-lg border border-gray-200 shadow-md">
              <div className="relative flex items-center bg-gray-50 rounded-lg border border-gray-200 focus-within:border-blue-300 focus-within:ring-1 focus-within:ring-blue-200 transition-all">
                <div className="relative">
                  <button
                    className="flex items-center gap-1 px-3 py-3 text-gray-600 hover:text-gray-800 border-r border-gray-200 cursor-pointer"
                    onClick={() => setShowSearchColumns(!showSearchColumns)}
                  >
                    <span className="text-sm whitespace-nowrap max-w-[100px] truncate">
                      {currentColumnLabel}
                    </span>
                    <svg 
                      className={`w-4 h-4 transition-transform ${showSearchColumns ? 'rotate-180' : ''}`} 
                      viewBox="0 0 24 24" 
                      fill="none"
                    >
                      <path d="M19 9l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                
                <input 
                  ref={mobileSearchInputRef}
                  type="text" 
                  placeholder={`Search ${currentColumnLabel.toLowerCase()}`}
                  className="w-full h-12 px-4 pr-10 bg-transparent text-gray-700 placeholder-gray-400 border-none focus:outline-none focus:ring-0"
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  value={searchInputValue}
                  autoComplete="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                />
                
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {searchLoading ? (
                    <Spinner size="sm" color="blue" />
                  ) : (
                    <svg 
                      className="w-4 h-4 text-gray-400" 
                      viewBox="0 0 24 24" 
                      fill="none"
                    >
                      <path 
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Section - Only show if hideTabBar is false */}
        {!hideTabBar && (
          <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center px-6 pb-4 gap-3">
            {/* Tabs - Only show if activeTab and onTabChange are provided */}
            {activeTab && onTabChange && (
              <>
                {/* Mobile collapsible tabs */}
                <div ref={mobileTabsRef} className="xl:hidden w-full">
                  <button
                    className="flex items-center justify-between w-full px-4 py-2 bg-gray-50 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                    onClick={() => setMobileTabsCollapsed(!mobileTabsCollapsed)}
                  >
                    <span className="font-medium">
                      {displayTabs.find(tab => tab.id === activeTab)?.label || displayTabs.find(tab => tab.id === activeTab)?.name || 'Select Tab'}
                    </span>
                    <svg 
                      className={`w-4 h-4 transition-transform ${mobileTabsCollapsed ? '' : 'rotate-180'}`} 
                      viewBox="0 0 24 24" 
                      fill="none"
                    >
                      <path d="M19 9l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  
                  {/* Mobile tabs dropdown - Will be converted to dialog */}
                  {!mobileTabsCollapsed && (
                    <div className="hidden mt-2 bg-white rounded-lg border border-gray-200 shadow-lg overflow-hidden">
                      {displayTabs.map(tab => (
                        <button
                          key={tab.id}
                          className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors border-b border-gray-100 last:border-b-0
                            ${activeTab === tab.id 
                              ? 'bg-blue-50 text-blue-600' 
                              : 'text-gray-600 hover:bg-gray-50'
                            }`}
                          onClick={() => {
                            onTabChange(tab.id);
                            setMobileTabsCollapsed(true);
                          }}
                        >
                          {tab.label || tab.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Desktop tabs - underline pill style */}
                <div className="hidden xl:flex items-center gap-1 overflow-x-auto hide-scrollbar w-full xl:w-auto border-b border-gray-100 -mb-px">
                  {displayTabs.map(tab => {
                    const isTabActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        className="relative px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap"
                        style={{ color: isTabActive ? buttonColors.primary : '#6b7280' }}
                        onMouseEnter={(e) => { if (!isTabActive) e.currentTarget.style.color = '#111827'; }}
                        onMouseLeave={(e) => { if (!isTabActive) e.currentTarget.style.color = '#6b7280'; }}
                        onClick={() => onTabChange(tab.id)}
                      >
                        {tab.label || tab.name}
                        {isTabActive && (
                          <span
                            className="absolute left-3 right-3 -bottom-px h-0.5 rounded-full"
                            style={{ backgroundColor: buttonColors.primary }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            
            {/* Action Buttons - Only show if hideActions is false */}
            {!hideActions && (
              <div className="flex gap-2 action-buttons w-full xl:w-auto mt-2 xl:mt-0">
                {/* Filters button + popover */}
                <div className="relative" ref={filtersPopoverRef}>
                  {(() => {
                    const activeCount =
                      (filters?.priority ? 1 : 0) +
                      (filters?.projectManager ? 1 : 0) +
                      (filters?.dateRange?.startDate || filters?.dateRange?.endDate ? 1 : 0);
                    return (
                      <button
                        className={`px-4 py-2 border rounded-lg text-sm font-medium shadow-sm flex items-center justify-center gap-2 transition-colors ${
                          activeCount > 0
                            ? 'bg-white text-gray-900'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                        }`}
                        style={activeCount > 0 ? { borderColor: buttonColors.primary, color: buttonColors.primary } : undefined}
                        type="button"
                        onClick={() => setShowFiltersPopover((v) => !v)}
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
                        </svg>
                        Filters
                        {activeCount > 0 && (
                          <span
                            className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white rounded-full"
                            style={{ backgroundColor: buttonColors.primary }}
                          >
                            {activeCount}
                          </span>
                        )}
                      </button>
                    );
                  })()}

                  {showFiltersPopover && (
                    <div
                      className="absolute right-0 mt-2 w-72 rounded-xl bg-white z-50 animate-in"
                      style={{
                        boxShadow:
                          '0 20px 50px -12px rgba(0,0,0,0.18), 0 8px 16px -8px rgba(0,0,0,0.10), 0 0 0 1px rgba(15,23,42,0.05)',
                      }}
                    >
                      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900">Filters</p>
                        {((filters?.priority) || (filters?.projectManager) || (filters?.dateRange?.startDate || filters?.dateRange?.endDate)) && (
                          <button
                            type="button"
                            className="text-xs font-medium hover:underline"
                            style={{ color: buttonColors.primary }}
                            onClick={() => { onClearAllFilters && onClearAllFilters(); }}
                          >
                            Clear all
                          </button>
                        )}
                      </div>

                      {/* Priority */}
                      {onPriorityChange && (
                        <div className="px-4 py-3 border-b border-gray-100">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Priority</p>
                          <div className="grid grid-cols-2 gap-1.5">
                            {[
                              { id: 'urgent', label: 'Urgent', dot: '#ef4444' },
                              { id: 'high', label: 'High', dot: '#f59e0b' },
                              { id: 'normal', label: 'Normal', dot: '#3b82f6' },
                              { id: 'low', label: 'Low', dot: '#9ca3af' },
                            ].map((p) => {
                              const isOn = filters?.priority === p.id;
                              return (
                                <button
                                  key={p.id}
                                  type="button"
                                  onClick={() => onPriorityChange(isOn ? null : p.id)}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                                    isOn
                                      ? 'bg-gray-50'
                                      : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                                  }`}
                                  style={isOn ? { borderColor: buttonColors.primary, color: buttonColors.primary } : undefined}
                                >
                                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.dot }} />
                                  {p.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Active filter chips */}
                      <div className="px-4 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Active filters</p>
                        <div className="flex flex-wrap gap-1.5">
                          {filters?.priority && (
                            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-100 text-xs font-medium text-gray-700">
                              Priority: {filters.priority}
                              <button
                                type="button"
                                className="hover:text-gray-900"
                                onClick={() => onPriorityChange && onPriorityChange(null)}
                              >
                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                  <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                              </button>
                            </span>
                          )}
                          {filters?.projectManager && (
                            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-100 text-xs font-medium text-gray-700">
                              PM: {filters.projectManager?.firstName || filters.projectManager?.profile?.firstName || 'Selected'}
                            </span>
                          )}
                          {(filters?.dateRange?.startDate || filters?.dateRange?.endDate) && (
                            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-100 text-xs font-medium text-gray-700">
                              Date range
                            </span>
                          )}
                          {!filters?.priority && !filters?.projectManager && !(filters?.dateRange?.startDate || filters?.dateRange?.endDate) && (
                            <span className="text-xs text-gray-400 italic">No filters applied</span>
                          )}
                        </div>
                        <p className="mt-3 text-[11px] text-gray-400 leading-relaxed">
                          Project manager & date range filters are available right above the table.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                {onCustomizeFields && (
                  <button 
                    className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors w-full xl:w-auto flex items-center justify-center gap-2 text-sm font-medium shadow-sm bg-white"
                    onClick={onCustomizeFields}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" />
                    </svg>
                    Customize
                  </button>
                )}

                {/* Action dropdown */}
                <div className="relative w-full xl:w-auto">
                  <button 
                    className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 w-full xl:w-auto"
                    onClick={() => setShowActionDropdown(!showActionDropdown)}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"/>
                    </svg>
                    Action
                    <svg 
                      className={`w-4 h-4 transform transition-transform ${showActionDropdown ? 'rotate-180' : ''}`} 
                      viewBox="0 0 24 24" 
                      fill="none"
                    >
                      <path d="M19 9l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>

                  {/* Dropdown Menu - Desktop only */}
                  {showActionDropdown && (
                    <div className="hidden xl:block absolute right-0 mt-2 w-48 bg-white rounded-lg border border-gray-200 shadow-lg z-10 py-1">
                      <button 
                        className="w-full px-4 py-2.5 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        onClick={() => {
                          handleEditClick();
                          setShowActionDropdown(false);
                        }}
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                        </svg>
                        Edit
                      </button>
                      <button 
                        className="w-full px-4 py-2.5 text-left text-red-600 hover:bg-red-50 flex items-center gap-2"
                        onClick={() => {
                          handleDeleteClick();
                          setShowActionDropdown(false);
                        }}
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                        </svg>
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </header>

      {/* Add NewLead dialog */}
      <NewLead 
        isOpen={showNewLead}
        onClose={() => setShowNewLead(false)}
      />

      {/* Add EditLead dialog */}
      {selectedLead && (
        <LeadDetailsDialog
          isOpen={showEditDialog}
          onClose={() => {
            setShowEditDialog(false);
            setSelectedLead(null);
          }}
          lead={selectedLead}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Delete {hideCreateLead ? 'Project' : 'Lead'}
              {(selectedItems.length > 0 ? selectedItems : selectedLeads).length > 1 ? 's' : ''}
            </h3>
            <p className="text-gray-500 mb-6">
              Are you sure you want to delete 
              {(selectedItems.length > 0 ? selectedItems : selectedLeads).length > 1 
                ? ` these ${hideCreateLead ? 'projects' : 'leads'}` 
                : ` this ${hideCreateLead ? 'project' : 'lead'}`}? 
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                onClick={() => setShowDeleteDialog(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg"
                onClick={handleDeleteConfirm}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Dialog */}
      <EditProfileDialog 
        isOpen={showEditProfile}
        onClose={() => setShowEditProfile(false)}
      />

      {/* Profile Menu Dialog for Mobile/Tablet */}
      {showProfileDropdown && (
        <div 
          className="xl:hidden fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50"
          onClick={() => setShowProfileDropdown(false)}
        >
          <div 
            className="bg-white rounded-t-xl w-full max-w-md mx-4 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Profile</h3>
                <button
                  onClick={() => setShowProfileDropdown(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
              <div className="mt-3">
                <p className="text-sm font-medium text-gray-900">
                  {`${user?.profile?.firstName} ${user?.profile?.lastName}`}
                </p>
                <p className="text-sm text-gray-500 mt-1">{user?.email}</p>
              </div>
            </div>
            
            <div className="py-2">
              <button 
                className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                onClick={() => {
                  setShowProfileDropdown(false);
                  setShowEditProfile(true);
                }}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                </svg>
                Edit Profile
              </button>
              
              <button 
                className="w-full px-4 py-3 text-left text-red-600 hover:bg-red-50 flex items-center gap-3"
                onClick={handleLogout}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
                </svg>
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search Columns Dialog for Mobile/Tablet */}
      {showSearchColumns && (
        <div 
          className="xl:hidden fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50"
          onClick={() => setShowSearchColumns(false)}
        >
          <div 
            className="bg-white rounded-t-xl w-full max-w-md mx-4 mb-4 animate-slide-up max-h-96 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Search In</h3>
                <button
                  onClick={() => setShowSearchColumns(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="py-2 max-h-64 overflow-y-auto">
              {searchColumns.map(column => (
                <button
                  key={column.id}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between ${
                    selectedSearchColumn === column.id 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-700'
                  }`}
                  onClick={() => handleSearchColumnSelect(column.id)}
                >
                  <span>{column.label}</span>
                  {selectedSearchColumn === column.id && (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tabs Dialog for Mobile/Tablet */}
      {!mobileTabsCollapsed && activeTab && onTabChange && (
        <div 
          className="xl:hidden fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50"
          onClick={() => setMobileTabsCollapsed(true)}
        >
          <div 
            className="bg-white rounded-t-xl w-full max-w-md mx-4 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Select Tab</h3>
                <button
                  onClick={() => setMobileTabsCollapsed(true)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="py-2">
              {displayTabs.map(tab => (
                <button
                  key={tab.id}
                  className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors flex items-center justify-between
                    ${activeTab === tab.id 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  onClick={() => {
                    onTabChange(tab.id);
                    setMobileTabsCollapsed(true);
                  }}
                >
                  <span>{tab.label || tab.name}</span>
                  {activeTab === tab.id && (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Actions Dialog for Mobile/Tablet */}
      {showActionDropdown && !hideActions && (
        <div 
          className="xl:hidden fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50"
          onClick={() => setShowActionDropdown(false)}
        >
          <div 
            className="bg-white rounded-t-xl w-full max-w-md mx-4 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Actions</h3>
                <button
                  onClick={() => setShowActionDropdown(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="py-2">
              <button 
                className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                onClick={() => {
                  handleEditClick();
                  setShowActionDropdown(false);
                }}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                </svg>
                Edit
              </button>
              <button 
                className="w-full px-4 py-3 text-left text-red-600 hover:bg-red-50 flex items-center gap-3"
                onClick={() => {
                  handleDeleteClick();
                  setShowActionDropdown(false);
                }}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Action Button for Mobile/Tablet - Create New Lead */}
      {!hideCreateLead && (
        <button
          className="xl:hidden fixed bottom-20 right-6 w-14 h-14 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center z-50 active:scale-95 group"
          style={{
            backgroundColor: buttonColors.primary,
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = buttonColors.primaryHover}
          onMouseLeave={(e) => e.target.style.backgroundColor = buttonColors.primary}
          onClick={onNewClick || (() => setShowNewLead(true))}
          aria-label={createButtonText}
        >
          <svg className="w-6 h-6 transition-transform group-hover:rotate-90 duration-200" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
          </svg>
          
          {/* Ripple effect on tap */}
          <div className="absolute inset-0 rounded-full bg-white opacity-0 group-active:opacity-20 transition-opacity duration-150"></div>
        </button>
      )}
    </>
  );
};

// Update prop types for better documentation
DashboardHeader.propTypes = {
  hideCreateLead: PropTypes.bool,
  onCustomizeFields: PropTypes.func,
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func,
  selectedItems: PropTypes.array,
  selectedLeads: PropTypes.array,
  onSearch: PropTypes.func,
  searchTerm: PropTypes.string,
  hideActions: PropTypes.bool,
  hideTabBar: PropTypes.bool,
  hideSearch: PropTypes.bool,
  items: PropTypes.array,
  leads: PropTypes.array,
  onDelete: PropTypes.func,
  tabs: PropTypes.array,
  createButtonText: PropTypes.string,
  onNewClick: PropTypes.func,
  visibleColumns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      key: PropTypes.string,
      label: PropTypes.string,
      name: PropTypes.string,
      checked: PropTypes.bool,
      active: PropTypes.bool
    })
  )
};

export default DashboardHeader; 